/**
 * TEACHER CONTENT STUDIO — Deskey Teacher Dashboard
 * Subject-centric studio with AI framework generation and @mention chat editing
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  BookOpen, Mic, FileText, Sparkles, Plus, Send, Clock,
  CheckCircle, Edit, Trash2, ChevronRight, ChevronDown, Users,
  LogOut, Globe, RefreshCw, Star, Brain, Layers, X,
  Target, Link2, Save, Library, Image, Menu, ChevronUp, PlayCircle,
  Settings, Eye, FolderOpen, Archive, Highlighter, Wand, ArrowDown,
  GraduationCap, ChevronLeft, Undo2, Redo2, Paperclip, Download, Film, Music, Play, Image2
} from '@/components/DeskeyIcons';

const API = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';

import { PREVIEW_THEMES } from './lib/previewThemes';
import { RenderContent, cleanText } from './components/RenderContent';
import DeskeyPaint from './components/DeskeyPaint';
import DeskeyChart from './components/DeskeyChart';
import TeacherMyClasses, { ClassDetailDrawer } from './components/TeacherMyClasses';
import PublishToLessonModal from './components/PublishToLessonModal';

// ── Table color system: presets + resolver ──
const TABLE_DEFAULT_COLORS = {
  header_bg: '#ecfdf5',
  header_text: '#065f46',
  accent: '#34A853',
  odd_row_bg: '#ffffff',
  even_row_bg: '#f8fafc',
  text: '#1e293b',
  border: '#34A853'
};

const TABLE_THEMES = [
  { id: 'emerald', label: 'Emerald', colors: { header_bg: '#ecfdf5', header_text: '#065f46', accent: '#34A853', odd_row_bg: '#ffffff', even_row_bg: '#f0fdf4', text: '#1e293b', border: '#34A853' } },
  { id: 'sapphire', label: 'Sapphire', colors: { header_bg: '#eff6ff', header_text: '#1e3a8a', accent: '#3b82f6', odd_row_bg: '#ffffff', even_row_bg: '#f0f9ff', text: '#1e293b', border: '#3b82f6' } },
  { id: 'amethyst', label: 'Amethyst', colors: { header_bg: '#faf5ff', header_text: '#6b21a8', accent: '#8b5cf6', odd_row_bg: '#ffffff', even_row_bg: '#f5f3ff', text: '#1e293b', border: '#8b5cf6' } },
  { id: 'ruby', label: 'Ruby', colors: { header_bg: '#fef2f2', header_text: '#991b1b', accent: '#dc2626', odd_row_bg: '#ffffff', even_row_bg: '#fff1f2', text: '#1e293b', border: '#dc2626' } },
  { id: 'amber', label: 'Amber', colors: { header_bg: '#fffbeb', header_text: '#78350f', accent: '#d97706', odd_row_bg: '#ffffff', even_row_bg: '#fef3c7', text: '#1e293b', border: '#d97706' } },
  { id: 'teal', label: 'Teal', colors: { header_bg: '#f0fdfa', header_text: '#134e4a', accent: '#0d9488', odd_row_bg: '#ffffff', even_row_bg: '#ccfbf1', text: '#1e293b', border: '#0d9488' } },
  { id: 'rose', label: 'Rose', colors: { header_bg: '#fff1f2', header_text: '#881337', accent: '#e11d48', odd_row_bg: '#ffffff', even_row_bg: '#ffe4e6', text: '#1e293b', border: '#e11d48' } },
  { id: 'slate', label: 'Slate', colors: { header_bg: '#f1f5f9', header_text: '#0f172a', accent: '#475569', odd_row_bg: '#ffffff', even_row_bg: '#f8fafc', text: '#1e293b', border: '#475569' } },
  { id: 'midnight', label: 'Midnight', colors: { header_bg: '#1e293b', header_text: '#f1f5f9', accent: '#38bdf8', odd_row_bg: '#0f172a', even_row_bg: '#1e293b', text: '#e2e8f0', border: '#334155' } },
];

const resolveTableColors = (table) => ({ ...TABLE_DEFAULT_COLORS, ...(table?.colors || {}) });

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api/teacher${path}`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; }
  catch { return { detail: text || `HTTP ${res.status}`, _status: res.status }; }
};
const apiUpload = async (path, fd) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api/teacher${path}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
  return res.json();
};

const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill={G} />
  </svg>
);

import SyllabusWorkspace from './components/SyllabusWorkspace';
import TeacherHubBrowser from './components/TeacherHubBrowser';
import EquationStudio from './components/teacher/EquationStudio';
import ImageGenHelper from './components/teacher/ImageGenHelper';
import SectionEquations from './components/teacher/SectionEquations';
import ModelPicker, { ModelBadge } from './components/ModelPicker';
import SafeImage from './components/SafeImage';
import ScientificSymbolPicker from './components/ScientificSymbolPicker';
import { FrameworkSection, AttachmentPreviewCard, AttachmentViewerModal, VisualEditorModal, ContentEditor } from './components/teacher/ContentEditor';
// import DigestSettings from './components/teacher/DigestSettings'; // digest card disabled for now
import AutoSyncMultimodalModal from './components/teacher/AutoSyncMultimodalModal';
import AutoSyncReviewPage from './components/teacher/AutoSyncReviewPage';
import SurrogateBanner from '@/components/SurrogateBanner';
import { Shield, Copy, Hash, Mail, UserPlus, ArrowLeft, Bell, MessageSquare, Calendar, Pencil, Square, Circle, ArrowRight, Type, Palette, Minus, Loader, Sigma, FunctionSquare, Atom, Beaker, FlaskConical } from 'lucide-react';
import { getSurrogateState, acceptSubstitution, enterSurrogateMode } from './lib/surrogate';
import { getSectionIcon } from './lib/sectionIcons';

// Section icons come from the shared registry in lib/sectionIcons.

const DIFF = { easy: { bg: '#DCFCE7', text: '#166534', l: 'Easy' }, medium: { bg: '#FEF9C3', text: '#854D0E', l: 'Medium' }, hard: { bg: '#FEE2E2', text: '#991B1B', l: 'Hard' } };

const SECTIONS_NAV = [
  { id: 'studio', label: 'Content Studio', icon: Sparkles },
  { id: 'syllabus', label: 'Syllabus', icon: GraduationCap },
  { id: 'hub', label: 'DeskeyHUB', icon: Library },
  { id: 'classes', label: 'My Classes', icon: Layers },
  { id: 'substitution', label: 'Substitution', icon: Shield },
  { id: 'published', label: 'Published', icon: CheckCircle },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// ── Session-card field preferences (saved per teacher via API; localStorage is a fast-start cache) ──
const CARD_FIELDS_KEY = 'desky_session_card_fields';
// Class & chapter always show on the card; only these extras are toggleable.
const CARD_FIELD_DEFS = [
  { key: 'subject', label: 'Subject' },
  { key: 'batch', label: 'Batch' },
  { key: 'department', label: 'Department' },
];
const DEFAULT_CARD_FIELDS = { subject: true, batch: false, department: false };
function loadCardFields() {
  try { return { ...DEFAULT_CARD_FIELDS, ...(JSON.parse(localStorage.getItem(CARD_FIELDS_KEY) || '{}')) }; }
  catch { return { ...DEFAULT_CARD_FIELDS }; }
}
// Resolve subject / batch / department for a class from the grouped-class tree.
function resolveClassMeta(classId, classGroups) {
  if (!classId) return null;
  for (const g of classGroups || []) {
    for (const b of g.batches || []) {
      const c = (b.classes || []).find(x => x.id === classId);
      if (c) return {
        subject: (c.subjects && c.subjects[0]) || '',
        batch: b.batch?.name || '',
        department: g.department?.name || '',
      };
    }
  }
  return null;
}

const COMMANDS = [
  { cmd: '/add', label: 'Add content', desc: 'Add new content to section' },
  { cmd: '/edit', label: 'Edit content', desc: 'Modify existing content' },
  { cmd: '/simplify', label: 'Simplify', desc: 'Make content simpler' },
  { cmd: '/expand', label: 'Expand', desc: 'Add more detail' },
  { cmd: '/examples', label: 'Add examples', desc: 'Add real-world examples' },
  { cmd: '/quiz', label: 'Add quiz', desc: 'Add practice questions' },
  { cmd: '/summarize', label: 'Summarize', desc: 'Create a summary' },
  // STEM-focused (these are interpreted by /studio/ai-chat in the backend)
  { cmd: '/equation', label: 'Insert equation', desc: 'Generate a LaTeX equation for this section' },
  { cmd: '/derive', label: 'Derive', desc: 'Show step-by-step derivation' },
  { cmd: '/solve', label: 'Solve step-by-step', desc: 'Worked solution with units' },
  { cmd: '/units', label: 'Add units & dimensions', desc: 'Annotate variables with SI units' },
  { cmd: '/balance', label: 'Balance reaction', desc: 'Balance a chemistry equation' },
  { cmd: '/reaction', label: 'Explain reaction', desc: 'Reactants → products with mechanism' },
  { cmd: '/diagram', label: 'Generate diagram', desc: 'AI image — labeled diagram' },
  { cmd: '/graph', label: 'Plot / graph', desc: 'Coordinate plot or function graph' },
  { cmd: '/board', label: 'Open board photos', desc: 'Add blackboard photos + OCR fusion' },
];

// Subject-adaptive prompt suggestions for the empty AI chat state.
// Returns 4 chips. The chips reference a section by @<title> so the AI knows what to edit.
function getSubjectSuggestions(subject, sections) {
  const first = sections?.[0]?.title || 'Overview';
  const second = sections?.[1]?.title || 'Key Concepts';
  const s = (subject || '').toLowerCase();
  if (['math', 'mathematics'].includes(s)) {
    return [
      { label: `@${first} /equation insert main formula`, type: 'math' },
      { label: `@${first} /solve a worked example`,        type: 'math' },
      { label: `@${second} /graph plot the function`,       type: 'math' },
      { label: `@${first} /derive the proof step-by-step`,  type: 'math' },
    ];
  }
  if (['physics'].includes(s)) {
    return [
      { label: `@${first} /equation Newton's 2nd law with units`, type: 'math' },
      { label: `@${first} /diagram free-body diagram`,           type: 'image' },
      { label: `@${second} /solve numerical with SI units`,       type: 'math' },
      { label: `@${first} /units list variables and SI units`,    type: 'math' },
    ];
  }
  if (['chemistry'].includes(s)) {
    return [
      { label: `@${first} /balance the chemical equation`,        type: 'chem' },
      { label: `@${first} /reaction explain mechanism`,           type: 'chem' },
      { label: `@${second} /diagram lab apparatus setup`,         type: 'image' },
      { label: `@${first} /equation rate law expression`,         type: 'chem' },
    ];
  }
  if (['biology'].includes(s)) {
    return [
      { label: `@${first} /diagram labeled cell diagram`,         type: 'image' },
      { label: `@${first} /expand with real-world examples`,      type: 'text' },
      { label: `@${second} simplify for grade 9`,                 type: 'text' },
      { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
    ];
  }
  if (['computer_science', 'cs', 'programming', 'computing'].includes(s)) {
    return [
      { label: `@${first} add a code example`,                    type: 'text' },
      { label: `@${first} explain the algorithm step-by-step`,    type: 'text' },
      { label: `@${second} /diagram flowchart`,                   type: 'image' },
      { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
    ];
  }
  // Default (history, english, social, etc.)
  return [
    { label: `@${first} simplify for grade 9`,                    type: 'text' },
    { label: `/expand with real-world examples`,                  type: 'text' },
    { label: `@${second} /diagram create illustration`,           type: 'image' },
    { label: `@${first} /quiz 5 practice questions`,              type: 'text' },
  ];
}

// Subject-aware quick-insert chips for the editor toolbar
function getSubjectQuickActions(subject) {
  const s = (subject || '').toLowerCase();
  if (['math', 'mathematics'].includes(s)) {
    return [
      { id: 'equation', label: 'Equation', icon: FunctionSquare, kind: 'eq' },
      { id: 'graph',    label: 'Graph',    icon: 'svg-graph',    kind: 'chat', text: '/graph plot ' },
      { id: 'solve',    label: 'Worked example', icon: 'svg-list', kind: 'chat', text: '/solve ' },
    ];
  }
  if (['physics'].includes(s)) {
    return [
      { id: 'equation', label: 'Equation',     icon: FunctionSquare, kind: 'eq' },
      { id: 'diagram',  label: 'Diagram',      icon: 'svg-graph',    kind: 'img' },
      { id: 'units',    label: 'Units',        icon: 'svg-list',     kind: 'chat', text: '/units ' },
    ];
  }
  if (['chemistry'].includes(s)) {
    return [
      { id: 'reaction', label: 'Balance reaction', icon: FlaskConical, kind: 'eq' },
      { id: 'mechanism',label: 'Mechanism',        icon: Beaker,       kind: 'chat', text: '/reaction ' },
      { id: 'diagram',  label: 'Lab setup',        icon: 'svg-graph',  kind: 'img' },
    ];
  }
  if (['biology'].includes(s)) {
    return [
      { id: 'diagram',  label: 'Diagram',  icon: 'svg-graph', kind: 'img' },
      { id: 'process',  label: 'Process',  icon: 'svg-list',  kind: 'chat', text: '/expand explain process step-by-step ' },
    ];
  }
  return [];
}

/* ═══════════════════════════════════════════════════════════════════════════
   TOP-RIGHT AVATAR MENU — replaces the old bottom-of-sidebar avatar/name/
   logout block. Click the avatar for a small overlay card with name, email,
   the "Teacher" role badge, and a themed logout action (confirm-gated).
   ═══════════════════════════════════════════════════════════════════════════ */
function TeacherAvatarMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = (user?.full_name || 'T').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleLogoutClick = async () => {
    setOpen(false);
    const ok = await confirmDialog({ title: 'Log out', message: 'You’ll need to sign in again to access Deskey Teacher.', confirmLabel: 'Log out', danger: true });
    if (ok) onLogout();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white transition-transform active:scale-95 hover:shadow-md"
        style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}
        title={user?.full_name}
        data-testid="teacher-avatar-menu-trigger"
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] w-64 rounded-2xl bg-white border border-slate-200 overflow-hidden z-50"
          style={{ boxShadow: '0 20px 50px -12px rgba(15,23,42,0.22), 0 6px 18px -6px rgba(15,23,42,0.10)' }}
          data-testid="teacher-avatar-menu-panel"
        >
          <div className="p-4 flex items-start gap-3 bg-gradient-to-br from-[#34A853]/6 via-white to-[#0d9488]/6">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-slate-900 truncate">{user?.full_name || 'Teacher'}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
              <span className="inline-block mt-1.5 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34A853]/10 text-[#166534] border border-[#34A853]/20">Teacher</span>
            </div>
          </div>
          <div className="h-px bg-slate-100" />
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-[13px] font-semibold text-red-600 hover:bg-red-50 transition-colors"
            data-testid="teacher-avatar-menu-logout"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
export default function TeacherDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [section, setSection] = useState('studio');
  const [dashboard, setDashboard] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  const [reviewSession, setReviewSession] = useState(null); // Auto Sync session being reviewed before generation
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState(null);
  const [subCount, setSubCount] = useState(0);
  const [cardFields, setCardFields] = useState(loadCardFields);
  const updateCardFields = (patch) => {
    const next = { ...cardFields, ...patch };
    setCardFields(next);
    try { localStorage.setItem(CARD_FIELDS_KEY, JSON.stringify(next)); } catch { /* ignore storage errors */ }
    api('/preferences/session-card', { method: 'PUT', body: JSON.stringify({ fields: next }) })
      .then(r => { if (r?.detail) toast.error('Could not save card settings'); })
      .catch(() => toast.error('Could not save card settings'));
  };
  // Hydrate card-field prefs from the server so they follow the teacher across devices
  useEffect(() => {
    api('/preferences/session-card').then(r => {
      if (r?.fields) {
        const next = { ...DEFAULT_CARD_FIELDS, ...r.fields };
        setCardFields(next);
        try { localStorage.setItem(CARD_FIELDS_KEY, JSON.stringify(next)); } catch { /* ignore storage errors */ }
      }
    }).catch(() => { /* offline — keep the local cache */ });
  }, []);

  const user = JSON.parse(localStorage.getItem('desky_user') || '{}');
  const token = localStorage.getItem('desky_token');

  const loadDashboard = useCallback(async () => {
    try {
      const [dash, draftRes, subRes] = await Promise.all([
        api('/studio/dashboard'), api('/studio/drafts'), api('/studio/subjects'),
      ]);
      setDashboard(dash);
      setDrafts(draftRes.drafts || []);
      setSubjects(subRes.subjects || []);
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, []);

  // Fetch active substitution count for sidebar badge
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/substitutions/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setSubCount(Array.isArray(d) ? d.length : 0)).catch(() => {});
  }, [token]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (activeDraft) setSidebarOpen(false); else setSidebarOpen(true); }, [activeDraft]);

  // Deep-link: ?draft=<id> opens that draft directly in the editor (new-tab UX)
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const draftId = params.get('draft');
    if (!draftId) return;
    if (activeDraft?.id === draftId) return;
    (async () => {
      const existing = drafts.find(d => d.id === draftId);
      if (existing) { setSection('studio'); setActiveDraft(existing); return; }
      try {
        const r = await api(`/studio/drafts/${draftId}`);
        if (r?.id) { setSection('studio'); setActiveDraft(r); }
        else toast.error('Session not found');
      } catch { toast.error('Could not load session'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.search, drafts]);

  const stats = dashboard?.stats || {};
  const classes = dashboard?.classes || [];

  const handleLogout = () => { localStorage.removeItem('desky_token'); localStorage.removeItem('desky_user'); navigate('/mydeskey'); };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center">
        <div className="relative w-16 h-16 mb-8" style={{ animation: 'deskyFloat 3s ease-in-out infinite' }}>
          <Logo size={56} />
        </div>
        <h2 className="text-sm font-bold text-gray-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Desky Studio</h2>
        <p className="text-[11px] text-gray-400 mt-1.5 font-medium tracking-wide">Loading workspace</p>
        <div className="mt-6 w-32 h-[2px] bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ background: '#34A853', animation: 'deskyBar 2s ease-in-out infinite' }} />
        </div>
        <style>{`
          @keyframes deskyFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          @keyframes deskyBar { 0% { width: 0; margin-left: 0; } 50% { width: 60%; margin-left: 20%; } 100% { width: 0; margin-left: 100%; } }
        `}</style>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex bg-[#f8f9fa] overflow-hidden" data-testid="teacher-dashboard">
      {activeDraft && sidebarOpen && <div className="fixed inset-0 z-30 bg-black/30" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />}

      <aside className={`flex-shrink-0 bg-[#1a1f36] flex flex-col z-40 transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-[220px]' : 'w-0 overflow-hidden'} ${activeDraft ? 'fixed inset-y-0 left-0' : 'relative'}`} data-testid="teacher-sidebar">
        <div className="w-[220px] h-full flex flex-col">
          <div className="px-4 py-5 flex items-center gap-2.5 border-b border-white/10">
            <Logo size={26} /><span className="text-[15px] font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Deskey</span>
            <span className="ml-auto text-[9px] font-bold text-white/40 uppercase tracking-wider">Studio</span>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {SECTIONS_NAV.map(s => (
              <button key={s.id} onClick={() => { setSection(s.id); setActiveDraft(null); setActiveSubject(null); setReviewSession(null); setSidebarOpen(true); }}
                className={`sidebar-nav-item ${section === s.id ? 'active' : ''}`} data-testid={`nav-${s.id}`}>
                <s.icon className="w-[18px] h-[18px]" />
                <span>{s.label}</span>
                {s.id === 'substitution' && subCount > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-500 text-white min-w-[18px] text-center animate-pulse">{subCount}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Scoped to the teacher's own dashboard only (this component is
            only reachable at /teacher/* behind ProtectedRoute) — renders
            when THIS teacher is inside a Surrogate Mode session, or has an
            accepted, in-window substitution ready to enter. Other
            teachers, students, admins, and the public landing/login pages
            never mount this component at all. */}
        <SurrogateBanner />
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6 gap-4 flex-shrink-0" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {!sidebarOpen && <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" data-testid="sidebar-toggle"><Menu className="w-5 h-5" /></button>}
          <h1 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {activeSubject ? activeSubject.name : activeDraft ? 'Content Editor' : reviewSession ? 'Session Review' : SECTIONS_NAV.find(s => s.id === section)?.label || 'Studio'}
          </h1>
          {activeSubject && !activeDraft && <button onClick={() => setActiveSubject(null)} className="text-xs text-gray-500 hover:text-gray-700">Back to Subjects</button>}
          <div className="ml-auto flex items-center gap-3">
            <NotificationBell token={token} />
            <div className="text-xs text-gray-500 font-medium">
              <span className="text-gray-900 font-bold">{stats.total_classes || 0}</span> classes
              <span className="mx-2 text-gray-300">|</span>
              {/* Server-side count: studio subjects UNION class-assigned ones.
                  `subjects` here is only the studio-authored list, which is 0
                  for teachers who just teach class subjects. */}
              <span className="text-gray-900 font-bold">{stats.total_subjects ?? subjects.length}</span> subjects
            </div>
            <TeacherAvatarMenu user={user} onLogout={handleLogout} />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {section === 'studio' && !activeDraft && !activeSubject && !reviewSession && (
            <StudioHome subjects={subjects} setSubjects={setSubjects} setActiveSubject={setActiveSubject} setActiveDraft={setActiveDraft} setReviewSession={setReviewSession} stats={stats} reload={loadDashboard} cardFields={cardFields} />
          )}
          {section === 'studio' && !activeDraft && !activeSubject && reviewSession && (
            <AutoSyncReviewPage
              session={reviewSession}
              goBack={() => setReviewSession(null)}
              onGenerated={(draft) => { setReviewSession(null); setActiveDraft(draft); loadDashboard(); }}
            />
          )}
          {section === 'studio' && !activeDraft && activeSubject && (
            <SubjectView subject={activeSubject} drafts={drafts.filter(d => d.subject_id === activeSubject.id)} setActiveDraft={setActiveDraft} classes={classes} reload={loadDashboard} />
          )}
          {section === 'studio' && activeDraft && (
            <ContentEditor draft={activeDraft} setDraft={setActiveDraft} classes={classes} reload={loadDashboard} goBack={() => { setActiveDraft(null); loadDashboard(); }} />
          )}
          {section === 'classes' && <TeacherMyClasses />}
          {section === 'substitution' && <SubstitutionSection token={token} classes={classes} />}
          {section === 'published' && <PublishedContent />}
          {section === 'syllabus' && <SyllabusWorkspace />}
          {section === 'hub' && <TeacherHubBrowser />}
          {section === 'settings' && <SettingsSection fields={cardFields} onChange={updateCardFields} />}
        </div>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STUDIO HOME — Subject Cards
   ═══════════════════════════════════════════════════════════════════════════ */
// Smart-note phases that mean the auto-sync pipeline is still running. The
// session's own `status` flips to 'ready' right after transcription, so
// multimodal items must also be checked against smart_note_status.
const SMART_NOTE_ACTIVE = ['queued', 'transcribing', 'ocr_running', 'fusing'];
const isSmartNoteBusy = (i) => i.auto_sync_multimodal && SMART_NOTE_ACTIVE.includes(i.smart_note_status);
const isProcessingItem = (i) => i.status === 'uploaded' || i.status === 'transcribing' || isSmartNoteBusy(i);

function StudioHome({ subjects, setSubjects, setActiveSubject, setActiveDraft, setReviewSession, stats, reload, cardFields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [classGroups, setClassGroups] = useState([]);
  const [routeFor, setRouteFor] = useState(null); // item being routed to a class + chapter
  const [opening, setOpening] = useState(null);
  const [autoSyncOpen, setAutoSyncOpen] = useState(false);
  const pollRef = useRef(null);
  const audioInputRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      const r = await api('/audio-inbox');
      if (r.items) setItems(r.items);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadInbox();
    (async () => {
      try { const r = await api('/classes/grouped'); if (r.groups) setClassGroups(r.groups); } catch { /* silent */ }
    })();
  }, [loadInbox]);

  // Poll while any item is still processing
  useEffect(() => {
    const hasProcessing = items.some(isProcessingItem);
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(loadInbox, 4000);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
      // Processing just finished — pick up drafts auto-created by the pipeline.
      reload();
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, loadInbox, reload]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('File too large. Max 100 MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source', 'web');
    try {
      const token = localStorage.getItem('desky_token') || '';
      const res = await fetch(`${API}/api/teacher/audio-inbox/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const d = await res.json();
      if (d.item) {
        toast.success('Audio received — transcribing…');
        setItems(prev => [d.item, ...prev]);
      } else toast.error(d.detail || 'Upload failed');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleOpen = async (item) => {
    if (item.status === 'error') { toast.error(item.error || 'Processing failed. Retry or delete.'); return; }
    if (isSmartNoteBusy(item)) {
      toast('Smart Note still processing…', { description: 'Voice and boards are being fused — a moment more.' });
      return;
    }
    if (item.status !== 'ready' && item.status !== 'drafted' && item.status !== 'published') {
      toast('Still transcribing…', { description: 'Give it a moment — this usually takes 20-60s.' });
      return;
    }
    // Auto Sync multimodal sessions without a draft yet open the review page:
    // transcript + board text side by side, then an explicit "Generate Session".
    if (item.auto_sync_multimodal && !item.draft_id) {
      setReviewSession(item);
      return;
    }
    setOpening(item.id);
    try {
      const r = await api(`/audio-inbox/${item.id}/generate-draft`, { method: 'POST' });
      if (!r.draft_id) { toast.error(r.detail || 'Could not create draft'); setOpening(null); return; }
      // Fetch full draft and activate editor
      const draft = await api(`/studio/drafts/${r.draft_id}`).catch(() => null);
      if (draft?.id || r.draft_id) {
        setActiveDraft(draft?.id ? draft : { id: r.draft_id, title: item.detected_class_name || item.original_filename, transcript: item.transcript });
        loadInbox();
        if (!r.existing) reload(); // a fresh draft was created — refresh the Drafts count
      }
    } catch { toast.error('Failed to open'); }
    setOpening(null);
  };

  // One-step routing: class (reassign-class) + chapter (set-chapter) together.
  // `sel` is { class_id, lesson_id?, lesson_title? }; class_id null clears routing.
  const handleRoute = async (item, sel) => {
    if (sel.class_id !== (item.detected_class_id || null)) {
      const r = await api(`/audio-inbox/${item.id}/reassign-class`, { method: 'POST', body: JSON.stringify({ class_id: sel.class_id }) });
      if (!r.updated) { toast.error(r.detail || 'Failed'); return; }
    }
    if (sel.class_id && (sel.lesson_id || sel.lesson_title)) {
      const r = await api(`/audio-inbox/${item.id}/set-chapter`, {
        method: 'POST',
        body: JSON.stringify({ lesson_id: sel.lesson_id || null, lesson_title: sel.lesson_title || null }),
      });
      if (!r.updated) { toast.error(r.detail || 'Failed to set chapter'); return; }
      toast.success(`Filed under “${r.lesson_name || 'chapter'}”`);
    } else {
      toast.success(sel.class_id ? 'Class updated' : 'Routing cleared');
    }
    setRouteFor(null); loadInbox();
  };

  const handleDelete = async (item) => {
    const msg = item.draft_id ? 'Delete this session? Its draft will be deleted too.' : 'Delete this audio from inbox?';
    if (!(await confirmDialog({ title: 'Delete session', message: msg, confirmLabel: 'Delete', danger: true }))) return;
    const r = await api(`/audio-inbox/${item.id}`, { method: 'DELETE' });
    if (r.removed) {
      toast.success('Removed');
      setItems(prev => prev.filter(i => i.id !== item.id));
      reload(); // refresh the Drafts / Published counts
    } else toast.error(r.detail || 'Failed');
  };

  const handleRetry = async (item) => {
    const r = await api(`/audio-inbox/${item.id}/retry`, { method: 'POST' });
    if (r.retrying) { toast.success('Retrying'); loadInbox(); } else toast.error(r.detail || 'Failed');
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-7">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-1">Teacher · Content Studio</div>
          <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}>Your AI lesson factory</h2>
          <p className="text-[13px] text-slate-500 mt-1 font-medium max-w-2xl">Deskey transcribes each lecture, auto-routes it to the right class, and lets you publish a polished lesson in minutes.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={audioInputRef} type="file" className="hidden" accept=".mp3,.m4a,.wav,.webm,.ogg,.flac,.aac,.mp4,.mov,audio/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value=''; handleUpload(f); }} data-testid="audio-upload-input" />
          <Button onClick={() => setAutoSyncOpen(true)} disabled={uploading} className="h-11 px-6 rounded-2xl text-white text-sm font-bold shadow-[0_8px_24px_rgba(52,168,83,0.28)] hover:shadow-[0_10px_28px_rgba(52,168,83,0.4)] active:scale-[0.98] transition-all" style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }} data-testid="audio-upload-btn">
            <Mic className="w-4 h-4 mr-2" />{uploading ? 'Syncing…' : 'Auto Sync'}
          </Button>
        </div>
      </div>

      {/* Stats — premium tile design */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Synced',     val: items.length,                                                                  icon: Mic,         c: '#34A853', soft: 'rgba(52,168,83,0.10)' },
          { label: 'Processing', val: items.filter(isProcessingItem).length,                                          icon: Loader,      c: '#F59E0B', soft: 'rgba(245,158,11,0.10)' },
          { label: 'Drafts',     val: stats.drafts || 0,                                                             icon: Edit,        c: '#0d9488', soft: 'rgba(13,148,136,0.10)' },
          { label: 'Published',  val: stats.published || 0,                                                          icon: CheckCircle, c: '#059669', soft: 'rgba(5,150,105,0.10)' },
        ].map((s, i) => (
          <div key={i}
               className="group relative bg-white rounded-2xl border border-slate-100 p-5 transition-all duration-300 hover:-translate-y-0.5"
               style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.04), 0 8px 32px rgba(15,23,42,0.06)' }}>
            {/* Accent line */}
            <div className="absolute top-0 left-5 right-5 h-px rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${s.c}55, transparent)` }} />
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
                   style={{ background: s.soft, color: s.c }}>
                <s.icon className="w-[20px] h-[20px]" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-extrabold text-slate-900 tabular-nums leading-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>{s.val}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.12em]">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Deskey Sessions */}
      <div data-testid="audio-inbox">
        {/* Last-Synced-Lesson digest — disabled for now */}
        {/* <DigestSettings api={api} /> */}
        <div className="flex items-baseline justify-between mb-4 mt-4">
          <div className="flex items-baseline gap-2">
            <h3 className="text-[15px] font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>Deskey Sessions</h3>
            <span className="text-[11px] text-slate-400">· most recent first</span>
          </div>
          {items.length > 0 && (
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">{items.length} synced</span>
          )}
        </div>
        {loading ? (
          <div className="p-10 text-center text-[13px] text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div
            className="bg-white rounded-xl border border-dashed border-zinc-200 p-10 text-center cursor-pointer hover:border-zinc-300 hover:bg-zinc-50/60 transition-colors"
            onClick={() => setAutoSyncOpen(true)}
            data-testid="audio-inbox-empty"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 bg-zinc-100">
              <Mic className="w-5 h-5 text-zinc-500" />
            </div>
            <p className="text-[13px] font-semibold text-gray-800">No Deskey Sessions synced yet</p>
            <p className="text-[12px] text-gray-500 mt-1 max-w-md mx-auto leading-relaxed">Pair your Deskey device — lectures stream in, get transcribed, and route to the matching class automatically.</p>
            <p className="text-[11px] text-[#34A853] font-semibold mt-4">Sync a session manually →</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map(item => (
              <AudioInboxCard key={item.id}
                item={item} classGroups={classGroups} fields={cardFields}
                opening={opening === item.id}
                onOpen={() => handleOpen(item)}
                onRoute={() => setRouteFor(item)}
                onDelete={() => handleDelete(item)}
                onRetry={() => handleRetry(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Class & chapter picker — route the session in one step */}
      {routeFor && (
        <ClassChapterPickerModal
          item={routeFor}
          classGroups={classGroups}
          onClose={() => setRouteFor(null)}
          onSubmit={(sel) => handleRoute(routeFor, sel)}
        />
      )}

      {/* Auto Sync Multimodal popup — voice + board fusion */}
      <AutoSyncMultimodalModal
        open={autoSyncOpen}
        onClose={() => setAutoSyncOpen(false)}
        onQueued={() => loadInbox()} />
    </div>
  );
}

/* ───────────── Audio Inbox card ───────────── */
const STATUS_PILL = {
  uploaded: { label: 'Queued', bg: '#fef3c7', fg: '#92400e' },
  transcribing: { label: 'Transcribing…', bg: '#dbeafe', fg: '#1d4ed8' },
  ready: { label: 'Ready to edit', bg: '#dcfce7', fg: '#166534' },
  drafted: { label: 'Draft', bg: '#f3e8ff', fg: '#6b21a8' },
  published: { label: 'Published', bg: '#cffafe', fg: '#155e75' },
  error: { label: 'Error', bg: '#fee2e2', fg: '#991b1b' },
};

function AudioInboxCard({ item, classGroups, fields = DEFAULT_CARD_FIELDS, opening, onOpen, onRoute, onDelete, onRetry }) {
  const smartNoteBusy = isSmartNoteBusy(item);
  const needsReview = item.auto_sync_multimodal && !item.draft_id && !smartNoteBusy;
  const pill = smartNoteBusy
    ? { label: 'Processing…', bg: '#fef3c7', fg: '#92400e' }
    : (needsReview && item.status === 'ready')
      ? { label: 'Ready to review', bg: '#dcfce7', fg: '#166534' }
      : (STATUS_PILL[item.status] || STATUS_PILL.uploaded);
  const canOpen = !smartNoteBusy && (item.status === 'ready' || item.status === 'drafted' || item.status === 'published');
  const syncedAgo = relativeTime(item.last_synced_at || item.updated_at || item.created_at);
  const hasClass = !!item.detected_class_id;
  // Hide "universal/general" fallback label — show only meaningful classifications
  const subjectLabel = (item.subject && item.subject !== 'universal') ? item.subject_label : null;
  const classMeta = resolveClassMeta(item.detected_class_id, classGroups);
  // Prefer the class's configured subject; fall back to the auto-detected subject.
  const subjectValue = classMeta?.subject || subjectLabel;
  // Prefer the teacher's session title (mirrored from the draft on rename),
  // then class name, then detected subject; never the filename.
  const cardTitle = item.title
    || item.detected_class_name
    || (subjectLabel ? `${subjectLabel} Lesson` : 'Deskey Session');
  return (
    <div
      className={`bg-white rounded-2xl border ${canOpen ? 'border-slate-100 hover:border-emerald-200 cursor-pointer' : 'border-slate-100'} transition-all duration-300 overflow-hidden group hover:-translate-y-0.5`}
      style={{ boxShadow: canOpen ? '0 1px 0 rgba(15,23,42,0.04), 0 6px 24px rgba(15,23,42,0.05)' : '0 1px 0 rgba(15,23,42,0.03)' }}
      onClick={canOpen ? onOpen : undefined}
      data-testid={`audio-card-${item.id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100/60 group-hover:scale-105 transition-transform">
            <Mic className="w-[18px] h-[18px] text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md border" style={{ background: pill.bg, color: pill.fg, borderColor: pill.fg + '33' }}>{pill.label}</span>
              {/* Field order: Department, Batch, Class, Subject, Chapter.
                  Dept/batch/subject follow the Settings toggles; class & chapter always show. */}
              {fields.department && classMeta?.department && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-100">{classMeta.department}</span>}
              {fields.batch && classMeta?.batch && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100">{classMeta.batch}</span>}
              {hasClass
                ? <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100">{item.detected_class_name}</span>
                : <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">No class</span>}
              {fields.subject && subjectValue && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">{subjectValue}</span>}
              {hasClass && (item.detected_lesson_id
                ? <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-100">{item.detected_lesson_name}</span>
                : <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">No chapter</span>)}
            </div>
            <p className="text-[15px] font-extrabold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.015em' }}>{cardTitle}</p>
            <p className="text-[11px] text-slate-400 mt-0.5 font-medium">Synced · {syncedAgo}</p>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 p-1.5 rounded-lg hover:bg-rose-50 transition-all" title="Delete" data-testid={`audio-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>

        {/* Class & chapter routing — one compact control, always visible */}
        <div className="mt-3 rounded-xl bg-slate-50/80 border border-slate-100 px-3 py-2 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          {hasClass ? (
            <p className="flex-1 min-w-0 truncate text-[11px] text-slate-500 font-medium">
              {item.detected_class_method === 'manual' ? 'Manually routed' : `Auto-matched · ${Math.round((item.detected_class_confidence || 0) * 100)}%`}
              {!item.detected_lesson_id && <span className="text-amber-600 font-bold"> · needs a chapter</span>}
            </p>
          ) : (
            <p className="flex-1 min-w-0 truncate text-[11px] text-amber-600 font-bold">Not routed to a class yet</p>
          )}
          <button onClick={(e) => { e.stopPropagation(); onRoute(); }} className="text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-md transition-all flex-shrink-0" data-testid={`audio-route-${item.id}`}>{hasClass ? 'Change' : 'Pick class & chapter'}</button>
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          {canOpen ? (
            <button onClick={(e) => { e.stopPropagation(); onOpen(); }} disabled={opening} className="flex-1 text-[12px] font-bold text-white px-3 py-2.5 rounded-xl flex items-center gap-1.5 justify-center transition-all duration-200 active:scale-[0.98] disabled:opacity-50 shadow-[0_4px_12px_rgba(52,168,83,0.25)] hover:shadow-[0_6px_18px_rgba(52,168,83,0.35)]" style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }} data-testid={`audio-open-${item.id}`}>
              {opening ? 'Opening…' : (<><Edit className="w-3.5 h-3.5" /> {item.status === 'published' ? 'View session' : needsReview ? 'Review & Generate' : 'Edit & Publish'}</>)}
            </button>
          ) : item.status === 'error' ? (
            <button onClick={(e) => { e.stopPropagation(); onRetry(); }} className="flex-1 text-[12px] font-bold text-white bg-gradient-to-r from-rose-500 to-rose-600 hover:shadow-md px-3 py-2.5 rounded-xl flex items-center gap-1.5 justify-center transition-all active:scale-[0.98]" data-testid={`audio-retry-${item.id}`}>
              Retry sync
            </button>
          ) : (
            <div className="flex-1 text-center text-[11px] text-slate-400 py-2.5 flex items-center justify-center gap-1.5 font-medium">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="animate-spin"><circle cx="12" cy="12" r="10" strokeDasharray="16 48"/></svg>
              {smartNoteBusy ? 'Fusing voice + boards…' : 'Transcribing session…'}
            </div>
          )}
        </div>
        {item.status === 'error' && item.error && (
          <p className="mt-2 text-[10px] text-red-500 truncate" title={item.error}>{item.error}</p>
        )}
      </div>
    </div>
  );
}

/* ───────────── Class & chapter picker — route a Deskey Session in one step ───────────── */
function ClassChapterPickerModal({ item, classGroups, onClose, onSubmit }) {
  // Flatten the dept › batch › class tree into pickable rows
  const classOptions = useMemo(() => {
    const out = [];
    for (const g of classGroups || []) {
      for (const b of g.batches || []) {
        for (const c of b.classes || []) {
          out.push({ id: c.id, name: c.name, context: [b.batch?.name, g.department?.name].filter(Boolean).join(' · ') });
        }
      }
    }
    return out;
  }, [classGroups]);

  const [selectedClassId, setSelectedClassId] = useState(item.detected_class_id || null);
  const [loading, setLoading] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [mode, setMode] = useState('pick'); // 'pick' | 'new'
  const [selectedLessonId, setSelectedLessonId] = useState(item.detected_lesson_id || null);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);

  // Reload chapters whenever the selected class changes
  useEffect(() => {
    if (!selectedClassId) { setChapters([]); setSelectedLessonId(null); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api(`/classes/${selectedClassId}/lessons`).catch(() => ({}));
      if (!alive) return;
      const list = r.lessons || [];
      setChapters(list);
      const keep = selectedClassId === item.detected_class_id ? item.detected_lesson_id : null;
      if (keep && list.some(c => c.id === keep)) {
        setMode('pick'); setSelectedLessonId(keep);
      } else if (list.length > 0) {
        setMode('pick'); setSelectedLessonId(list[0].id);
      } else {
        setMode('new'); setSelectedLessonId(null);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [selectedClassId, item.detected_class_id, item.detected_lesson_id]);

  const chapterChoice = mode === 'pick'
    ? (selectedLessonId ? { lesson_id: selectedLessonId } : null)
    : (newTitle.trim() ? { lesson_title: newTitle.trim() } : null);
  const canSubmit = !!selectedClassId && !!chapterChoice;
  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    await onSubmit({ class_id: selectedClassId, ...chapterChoice });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[72] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="class-chapter-picker-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-lg font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Route this session</h3>
            <p className="text-[11px] text-gray-500 truncate">Pick the class and chapter — students see the session there.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Class */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5 mb-2"><Layers className="w-3.5 h-3.5" style={{ color: G }} /> Class</p>
            {classOptions.length === 0 ? (
              <div className="py-6 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">No classes assigned yet.</div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {classOptions.map(c => {
                  const active = selectedClassId === c.id;
                  return (
                    <button key={c.id} onClick={() => setSelectedClassId(c.id)}
                      className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${active ? 'border-[#34A853] bg-[#34A853]/5 ring-1 ring-[#34A853]/20' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
                      data-testid={`route-class-${c.id}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#34A853] text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                        {active ? <CheckCircle className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-gray-900 truncate">{c.name}</p>
                        {c.context && <p className="text-[11px] text-gray-500 truncate">{c.context}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" style={{ color: G }} /> Chapter</p>
            <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-0.5">
              <button onClick={() => setMode('pick')} disabled={chapters.length === 0}
                className={`text-[11px] font-semibold px-3 py-1 rounded-md transition-colors ${mode === 'pick' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                data-testid="chapter-mode-pick">Pick existing</button>
              <button onClick={() => setMode('new')}
                className={`text-[11px] font-semibold px-3 py-1 rounded-md transition-colors ${mode === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="chapter-mode-new">+ New chapter</button>
            </div>
          </div>

          {!selectedClassId ? (
            <div className="py-6 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">Choose a class above to see its chapters.</div>
          ) : mode === 'pick' ? (
            loading ? (
              <div className="py-8 text-center text-[12px] text-gray-400">Loading chapters…</div>
            ) : chapters.length === 0 ? (
              <div className="py-6 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">No chapters yet — switch to <span className="font-semibold">+ New chapter</span>.</div>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {chapters.map(ch => {
                  const active = selectedLessonId === ch.id;
                  return (
                    <button key={ch.id} onClick={() => setSelectedLessonId(ch.id)}
                      className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${active ? 'border-[#34A853] bg-[#34A853]/5 ring-1 ring-[#34A853]/20' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
                      data-testid={`chapter-pick-${ch.id}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#34A853] text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                        {active ? <CheckCircle className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-gray-900 truncate">{ch.title}</p>
                        <p className="text-[11px] text-gray-500">{ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}{ch.latest_topic_date ? ` · last on ${new Date(ch.latest_topic_date).toLocaleDateString()}` : ''}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div className="rounded-xl border border-[#34A853]/30 bg-[#34A853]/5 p-4">
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#0d9488] mb-1.5 block">New chapter name</label>
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Chapter 4 — Thermodynamics"
                className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 bg-white"
                data-testid="chapter-new-title" autoFocus />
              <p className="text-[11px] text-gray-500 mt-2">Chapters group your daily topics. You can rename / reorder later.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-zinc-100 bg-gray-50/60 flex items-center justify-between gap-2">
          {item.detected_class_id ? (
            <button onClick={() => onSubmit({ class_id: null })} className="text-[11px] font-semibold text-gray-500 hover:text-red-500" data-testid="route-clear">Clear routing</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-[12px] font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100">Cancel</button>
            <button onClick={submit} disabled={!canSubmit || saving}
              className="text-[12px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-5 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-colors"
              data-testid="route-confirm">{saving ? 'Saving…' : 'Save routing'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────── Settings — customize the session card ───────────── */
function SettingsSection({ fields, onChange }) {
  return (
    <div className="p-8 max-w-3xl mx-auto space-y-7">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-1">Teacher · Preferences</div>
        <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}>Settings</h2>
        <p className="text-[13px] text-slate-500 mt-1 font-medium">Customize what appears on your Deskey Session cards.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6" style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.04), 0 8px 32px rgba(15,23,42,0.06)' }}>
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-emerald-700" />
          <h3 className="text-[15px] font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Session card fields</h3>
        </div>
        <p className="text-[12px] text-slate-500 mb-4">Toggle which details show on each session card. Class and chapter always show. Saved to your account.</p>
        <div className="divide-y divide-slate-100">
          {CARD_FIELD_DEFS.map(f => {
            const on = !!fields[f.key];
            return (
              <div key={f.key} className="flex items-center justify-between py-3.5">
                <div>
                  <p className="text-[13px] font-bold text-slate-800">{f.label}</p>
                  <p className="text-[11px] text-slate-400">{on ? 'Shown on the card' : 'Hidden'}</p>
                </div>
                <button
                  role="switch" aria-checked={on}
                  onClick={() => onChange({ [f.key]: !on })}
                  data-testid={`card-field-toggle-${f.key}`}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${on ? 'bg-[#34A853]' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return 'just now';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUBJECT VIEW — Content list inside a subject
   ═══════════════════════════════════════════════════════════════════════════ */
function SubjectView({ subject, drafts, setActiveDraft, classes, reload }) {
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [subjectDrafts, setSubjectDrafts] = useState(drafts);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    api(`/studio/subjects/${subject.id}/content`).then(res => {
      setSubjectDrafts(res.content || []);
      setLoadingContent(false);
    }).catch(() => { setSubjectDrafts(drafts); setLoadingContent(false); });
  }, [subject.id, drafts]);

  const createDraft = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const draft = await api('/studio/drafts', { method: 'POST', body: JSON.stringify({ title: newTitle.trim(), subject_id: subject.id }) });
      if (draft.id) { setSubjectDrafts(prev => [draft, ...prev]); setActiveDraft(draft); setNewTitle(''); toast.success('Draft created'); reload(); }
    } catch { toast.error('Failed'); }
    setCreating(false);
  };

  const deleteDraft = async (id) => {
    try { await api(`/studio/drafts/${id}`, { method: 'DELETE' }); setSubjectDrafts(prev => prev.filter(d => d.id !== id)); toast.success('Deleted'); reload(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      {/* Subject Header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${G}14` }}>
            <BookOpen className="w-6 h-6" style={{ color: G }} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{subject.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{subjectDrafts.length} content items | {subjectDrafts.filter(d => d.status === 'published').length} published</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-center px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-[10px] text-gray-400 font-bold uppercase">Deskey App</p>
              <p className="text-[11px] text-gray-600 font-bold flex items-center gap-1"><PlayCircle className="w-3 h-3" style={{ color: G }} /> Start Recording</p>
            </div>
          </div>
        </div>
      </div>

      {/* New Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 className="text-sm font-bold text-gray-900 mb-3">New Content</h3>
        <div className="flex gap-3">
          <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && createDraft()}
            placeholder="Enter lesson title..." className="h-10 flex-1 rounded-lg text-sm" data-testid="new-draft-title" />
          <Button onClick={createDraft} disabled={creating || !newTitle.trim()} className="h-10 px-5 rounded-lg text-white text-sm font-bold" style={{ background: G }} data-testid="create-draft-btn">
            <Plus className="w-4 h-4 mr-1.5" />{creating ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </div>

      {/* Content List */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Content ({subjectDrafts.length})</h3>
        {subjectDrafts.length === 0 && !loadingContent ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-500">No content yet for this subject</p>
          </div>
        ) : (
          <div className="space-y-2">
            {subjectDrafts.map(d => (
              <div key={d.id} onClick={() => setActiveDraft(d)}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 group"
                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid={`draft-${d.id}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.status === 'published' ? 'bg-emerald-50' : (d.has_framework || d.framework) ? 'bg-blue-50' : 'bg-amber-50'}`}>
                  {d.status === 'published' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : (d.has_framework || d.framework) ? <Brain className="w-5 h-5 text-blue-600" /> : <Edit className="w-5 h-5 text-amber-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{d.title}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {d.status === 'published' ? 'Published' : (d.has_framework || d.framework) ? 'Framework Ready' : 'Draft'}
                    {d.key_topics?.length > 0 && <><span className="mx-1.5 text-gray-300">|</span>{d.key_topics.slice(0, 2).join(', ')}</>}
                  </p>
                </div>
                <button onClick={e => { e.stopPropagation(); deleteDraft(d.id); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-1"><Trash2 className="w-4 h-4" /></button>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FRAMEWORK SECTION — Expandable with inline edit + AI chat target
   ═══════════════════════════════════════════════════════════════════════════ */



/* ═══════════════════════════════════════════════════════════════════════════
   MY CLASSES / PUBLISHED / SCORES
   ═══════════════════════════════════════════════════════════════════════════ */
function NotificationBell({ token }) {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [n, c] = await Promise.all([
          fetch(`${API}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        setNotifs(n || []);
        setUnread(c?.count || 0);
      } catch {}
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const typeIcon = (type) => {
    if (type?.includes('substitution') || type?.includes('substitute')) return { icon: Shield, color: '#3B82F6' };
    if (type?.includes('access')) return { icon: MessageSquare, color: '#F59E0B' };
    return { icon: Bell, color: '#64748b' };
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-gray-500" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: '#EF4444', minWidth: 18, height: 18, padding: '0 4px' }}>{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-gray-100 z-50 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
              {unread > 0 && <button onClick={markAllRead} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium">Mark all read</button>}
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="py-10 text-center"><Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" /><p className="text-sm text-gray-400">No notifications</p></div>
              ) : notifs.slice(0, 20).map(n => {
                const { icon: Icon, color } = typeIcon(n.type);
                return (
                  <button key={n.id} onClick={() => { if (!n.read) markRead(n.id); }} className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${!n.read ? 'bg-blue-50/30' : ''}`} data-testid={`notif-${n.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}14` }}><Icon className="w-4 h-4" style={{ color }} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-gray-900">{n.title}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MyClasses({ classes: legacyClasses, token }) {
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [hiddenCards, setHiddenCards] = useState(() => JSON.parse(localStorage.getItem('desky_hidden_classes') || '[]'));
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ac, as2, subs] = await Promise.all([
          fetch(`${API}/api/classes/my-assigned`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/api/subjects/my-assigned`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/api/substitutions/my?include_ended=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        setAssignedClasses(ac.classes || []);
        setAssignedSubjects(as2 || []);
        setSubstitutions(subs || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [token]);

  const hideCard = (id) => {
    const updated = [...hiddenCards, id];
    setHiddenCards(updated);
    localStorage.setItem('desky_hidden_classes', JSON.stringify(updated));
  };

  const deleteEndedSub = async (subId) => {
    await fetch(`${API}/api/substitutions/${subId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setSubstitutions(prev => prev.filter(s => s.id !== subId));
  };

  // Merge: assigned classes (active) + legacy classes not in assigned (shown as unassigned)
  const allClasses = [...assignedClasses.map(c => ({ ...c, _active: true }))];
  legacyClasses.forEach(c => {
    if (!allClasses.find(a => a.id === c.id)) allClasses.push({ ...c, _active: false });
  });
  const visibleClasses = allClasses.filter(c => !hiddenCards.includes(c.id));

  const activeSubs = substitutions.filter(s => s.status === 'active');
  const endedSubs = substitutions.filter(s => s.status !== 'active');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="my-classes-section">
      <h2 className="text-xl font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Classes</h2>

      {loading ? <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 text-gray-300 animate-spin" /></div> : (
        <>
          {/* Active Substitution Banner */}
          {activeSubs.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4 flex items-center gap-3" data-testid="sub-active-banner">
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-blue-900">{activeSubs.length} Active Substitution{activeSubs.length > 1 ? 's' : ''}</p>
                <p className="text-[11px] text-blue-600">You are covering for other teachers. Use Deskey Studio to post content.</p>
              </div>
            </div>
          )}

          {visibleClasses.length === 0 && activeSubs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center"><Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No classes assigned</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visibleClasses.map(c => (
                <div key={c.id} className={`bg-white rounded-xl border p-4 relative group transition-all ${c._active ? 'border-gray-200 hover:shadow-md' : 'border-dashed border-gray-300 opacity-60'}`} data-testid={`class-card-${c.id}`}>
                  {!c._active && (
                    <button onClick={() => hideCard(c.id)} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all" title="Hide card" data-testid={`hide-class-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: c._active ? `${G}14` : '#f1f5f9' }}>
                      <BookOpen className="w-4 h-4" style={{ color: c._active ? G : '#94a3b8' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{c.name}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{c.class_code}</p>
                    </div>
                    <span className={`text-[9px] font-semibold uppercase px-2 py-0.5 rounded-full ${c._active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {c._active ? 'Active' : 'Unassigned'}
                    </span>
                  </div>
                  <div className="flex gap-3 text-[11px]">
                    <span className="text-gray-500"><span className="font-bold text-gray-700">{c.student_count || 0}</span> Students</span>
                    <span className="text-gray-500"><span className="font-bold text-gray-700">{c.lesson_count || 0}</span> Lessons</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Assigned Subjects */}
          {assignedSubjects.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-2">Assigned Subjects</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {assignedSubjects.map(s => (
                  <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3" data-testid={`assigned-subject-${s.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${G}14` }}><BookOpen className="w-3.5 h-3.5" style={{ color: G }} /></div>
                    <div className="flex-1 min-w-0"><p className="text-xs font-bold text-gray-900 truncate">{s.name}</p><p className="text-[10px] text-gray-500">{s.class_name}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Substitutions — Special Embossed UI */}
          {activeSubs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1.5"><Shield className="w-4 h-4 text-blue-500" /> Active Substitutions</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeSubs.map(sub => (
                  <div key={sub.id} className="relative rounded-xl p-4 border-2 border-blue-300 overflow-hidden group hover:shadow-lg transition-all" data-testid={`substitution-card-${sub.id}`}
                    style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 40%, #E0E7FF 100%)' }}>
                    {/* Decorative accent */}
                    <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #3B82F6, #6366F1)' }} />
                    <div className="absolute top-3 right-3">
                      <span className="text-[8px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-500 text-white tracking-wider">Substitute</span>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)' }}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{sub.subject_name}</p>
                        <p className="text-[10px] text-blue-700">{sub.class_name}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-[11px]">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Users className="w-3 h-3 text-blue-500" />
                        <span>Covering for <strong>{sub.original_teacher_name}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Calendar className="w-3 h-3 text-blue-500" />
                        <span>{sub.start_date} &mdash; {sub.end_date}</span>
                      </div>
                      {sub.reason && (
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FileText className="w-3 h-3 text-blue-400" />
                          <span className="italic">{sub.reason}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-blue-200/50">
                      <span className="text-[10px] text-blue-600 font-semibold bg-blue-100 px-2 py-0.5 rounded-full">{sub.lectures_posted || 0} lectures posted</span>
                      {!sub.accepted && (
                        <button
                          onClick={async () => {
                            try {
                              const { acceptSubstitution } = await import('./lib/surrogate');
                              const updated = await acceptSubstitution(sub.id);
                              toast.success('Assignment accepted');
                              setSubstitutions(prev => prev.map(s => s.id === sub.id ? { ...s, ...updated } : s));
                            } catch (e) { toast.error(e.message || 'Could not accept'); }
                          }}
                          className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-full transition-colors"
                          data-testid={`accept-substitution-${sub.id}`}
                        >Accept</button>
                      )}
                      {sub.accepted && (
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`accepted-badge-${sub.id}`}>Accepted</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ended/Unassigned Substitutions */}
          {endedSubs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-1.5">Past Substitutions</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {endedSubs.map(sub => (
                  <div key={sub.id} className="bg-gray-50 rounded-xl border border-gray-200 p-3 flex items-center gap-3 opacity-70 group" data-testid={`ended-sub-card-${sub.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-200"><Shield className="w-3.5 h-3.5 text-gray-500" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-700 truncate">{sub.subject_name}</p>
                      <p className="text-[10px] text-gray-400">{sub.start_date} &mdash; {sub.end_date}</p>
                    </div>
                    <span className="text-[9px] text-gray-400 px-1.5 py-0.5 rounded bg-gray-200 uppercase">{sub.status}</span>
                    <button onClick={() => deleteEndedSub(sub.id)} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" title="Delete record" data-testid={`delete-ended-sub-${sub.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubstitutionSection({ token, classes }) {
  const [tab, setTab] = useState('active');
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);
  const [enteringId, setEnteringId] = useState(null);
  const [surrogateState, setSurrogateState] = useState(() => getSurrogateState());

  const load = useCallback(async () => {
    try {
      const s = await fetch(`${API}/api/substitutions/my?include_ended=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setSubs(Array.isArray(s) ? s : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSurrogateState(getSurrogateState()); }, []);

  const handleAccept = async (subId) => {
    setAcceptingId(subId);
    try {
      const updated = await acceptSubstitution(subId);
      toast.success('Assignment accepted — you can now enter Surrogate Mode');
      setSubs(prev => prev.map(s => (s.id === subId ? { ...s, ...updated } : s)));
    } catch (e) {
      toast.error(e.message || 'Could not accept');
    }
    setAcceptingId(null);
  };

  const handleEnter = async (subId) => {
    setEnteringId(subId);
    try {
      const res = await enterSurrogateMode(subId);
      toast.success(`Now acting for ${res.surrogate_for_name}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.message || 'Could not enter surrogate mode');
    }
    setEnteringId(null);
  };

  const deleteEndedSub = async (subId) => {
    await fetch(`${API}/api/substitutions/${subId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const activeSubs = subs.filter(s => s.status === 'active');
  const endedSubs = subs.filter(s => s.status !== 'active');
  const today = new Date().toISOString().slice(0, 10);
  const isInWindow = (s) => s.start_date <= today && today <= s.end_date;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="substitution-section">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Substitutions</h2>
        <p className="text-[12px] text-gray-500 mt-0.5">Accept an assignment, then enter Surrogate Mode to act as the original teacher.</p>
      </div>

      {surrogateState.active && surrogateState.meta && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #EA580C)' }}>
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900">You're currently acting for {surrogateState.meta.surrogate_for_name}</p>
            <p className="text-xs text-amber-700">Subject: {surrogateState.meta.subject_name} &middot; until {surrogateState.meta.end_date}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100/60 rounded-xl w-fit">
        {[
          { id: 'active', label: `Active (${activeSubs.length})` },
          { id: 'ended', label: `Past (${endedSubs.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`sub-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 text-gray-300 animate-spin" /></div> : null}

      {/* Active Substitutions */}
      {!loading && tab === 'active' && (
        <div className="space-y-4">
          {activeSubs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center"><Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No active substitutions</p></div>
          ) : activeSubs.map(sub => {
            const inWindow = isInWindow(sub);
            const canEnter = sub.accepted && inWindow && !sub.revoked_at && !surrogateState.active;
            return (
              <div key={sub.id} className="bg-white rounded-xl border-2 border-blue-200 p-5 space-y-4" data-testid={`sub-card-${sub.id}`}
                style={{ background: 'linear-gradient(135deg, #FAFBFF 0%, #F0F4FF 100%)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)' }}>
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">{sub.subject_name}</p>
                    <p className="text-[11px] text-gray-500">{sub.class_name} &middot; Covering for <strong>{sub.original_teacher_name}</strong> &middot; {sub.start_date} &mdash; {sub.end_date}</p>
                  </div>
                  {sub.accepted ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`accepted-badge-${sub.id}`}>Accepted</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Pending</span>
                  )}
                </div>

                {sub.reason && (
                  <p className="text-[12px] text-gray-600 bg-white/70 border border-blue-100 rounded-lg px-3 py-2 italic" data-testid={`sub-reason-${sub.id}`}>
                    "{sub.reason}"
                  </p>
                )}

                {!inWindow && (
                  <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {today < sub.start_date ? `Starts ${sub.start_date}` : `Ended ${sub.end_date}`}
                  </p>
                )}

                <div className="flex gap-2">
                  {!sub.accepted ? (
                    <Button
                      onClick={() => handleAccept(sub.id)}
                      disabled={acceptingId === sub.id || !inWindow}
                      size="sm" className="h-8 px-4 rounded-lg text-white text-xs active:scale-95"
                      style={{ background: G }} data-testid={`accept-substitution-${sub.id}`}
                    >
                      {acceptingId === sub.id ? 'Accepting…' : 'Accept'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleEnter(sub.id)}
                      disabled={!canEnter || enteringId === sub.id}
                      size="sm" className="h-8 px-4 rounded-lg text-white text-xs active:scale-95"
                      style={{ background: 'linear-gradient(135deg, #F59E0B, #EA580C)' }}
                      data-testid={`enter-surrogate-${sub.id}`}
                    >
                      <Shield className="w-3.5 h-3.5 mr-1" />
                      {enteringId === sub.id ? 'Entering…' : surrogateState.active ? 'Exit current session first' : 'Enter Surrogate Mode'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ended/Unassigned Substitutions */}
      {!loading && tab === 'ended' && (
        <div className="space-y-3">
          {endedSubs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center"><Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No past substitutions</p></div>
          ) : endedSubs.map(sub => (
            <div key={sub.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 group" data-testid={`ended-sub-${sub.id}`}>
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center"><Shield className="w-4 h-4 text-gray-400" /></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">{sub.subject_name}</p>
                <p className="text-[11px] text-gray-400">{sub.class_name} &middot; {sub.original_teacher_name} &middot; {sub.start_date} &mdash; {sub.end_date}</p>
              </div>
              <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{sub.status}</span>
              <button onClick={() => deleteEndedSub(sub.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-sub-${sub.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PublishedContent() {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api('/studio/drafts?status=published').then(r => { setItems(r.drafts || []); setLoading(false); }); }, []);
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <h2 className="text-xl font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Published Content</h2>
      {items.length === 0 && !loading ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}><CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm font-bold text-gray-500">No published content</p></div> : (
        <div className="space-y-2">{items.map(d => (
          <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
            <div className="flex-1 min-w-0"><p className="text-sm font-bold text-gray-900 truncate">{d.title}</p><p className="text-[11px] text-gray-500">{d.published_at ? `Published ${new Date(d.published_at).toLocaleDateString('en-IN')}` : 'Published'}</p></div>
          </div>
        ))}</div>
      )}
    </div>
  );
}
